<template>
  <AnalyticsWidget
    title="DIFOT Details"
    subTitle="DIFOT Details based on orders during the date range"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    >
    <template v-slot:title>
      <ReportFilterPopover
        class="float-right mr-2"
        headerTitle="Report Filter"
        :showApplyButton="true"
        :showResetButton="true"
        :appendToBody="true"
        @apply="applySummaryFilter"
        @reset="resetSummaryFilter">
        <template v-slot:popover>
          <ReportFilterGroup label="Date Range">
            <ReportDateRangeSelect 
              :startDate="editingSummaryFilter.startDate"
              :endDate="editingSummaryFilter.endDate"
              @updateStartDate="editingSummaryFilter.startDate = $event"
              @updateEndDate="editingSummaryFilter.endDate = $event"
            />
          </ReportFilterGroup>
        </template>
        <template v-slot:filterDisplay>
          <div>
            <i class="far fa-calendar mr-2"></i>{{appliedSummaryFilter.startDate | formatDate}} - {{appliedSummaryFilter.endDate | formatDate}}
          </div>
        </template>
      </ReportFilterPopover>
    </template>
    <div class="p-2">
      <LoadingPanel :loading="loading" />
      <AnalyticsBreakdownTable v-if="!loading">
        <template v-slot:header>
          <tr>
            <th>Info</th>
            <th>Orders Completed</th>
            <th>Operational Score</th>
            <th>Commercial Score</th>
            <th><Tooltip label="Orders that were on time or early" helpStyle>Hit</Tooltip></th>
            <th><Tooltip label="Orders that were late due to operational reasons" helpStyle>Miss</Tooltip></th>
            <th><Tooltip label="Orders that were late due to external reasons &amp; excluded" helpStyle>Exclude</Tooltip></th>
            <th><Tooltip label="Orders that had no reporting at all and was not classified" helpStyle>N/A</Tooltip></th>
          </tr>
        </template>
        <template v-slot:body>
          <tr>
            <td>Overall</td>
            <td>{{totalSummaryRow.orderCount}}</td>
            <td>{{ formatPercent(totalSummaryRow.operationalDIFOT) }}</td>
            <td>{{ formatPercent(totalSummaryRow.commercialDIFOT) }}</td>
            <td>{{ totalSummaryRow.hitCount }}</td>
            <td>{{ totalSummaryRow.missCount }}</td>
            <td>{{ totalSummaryRow.excludeCount }}</td>
            <td>{{ totalSummaryRow.nonClassifiedCount }}</td>
          </tr>
          <tr v-for="taskTypeInfo of taskTypeSummaryList" :key="taskTypeInfo.id">
            <td>{{ taskTypeInfo.name }}</td>
            <td>{{ taskTypeInfo.orderCount }}</td>
            <td>{{ formatPercent(taskTypeInfo.operationalDIFOT) }}</td>
            <td>{{ formatPercent(taskTypeInfo.commercialDIFOT) }}</td>
            <td>{{ taskTypeInfo.hitCount }}</td>
            <td>{{ taskTypeInfo.missCount }}</td>
            <td>{{ taskTypeInfo.excludeCount }}</td>
            <td>{{ taskTypeInfo.nonClassifiedCount }}</td>
          </tr>
        </template>
      </AnalyticsBreakdownTable>
    </div>
  </AnalyticsWidget>
</template>


<script>
import moment from 'moment'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'

import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'
import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

export default {
  name: 'OrderDIFOTDetailsWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup
  },
  props: {
    filter: Object,
    gridWidth: Number,
    gridHeight: Number
  },
  computed: {
    taskTypeList() {
      return this.summaryData.taskTypeList || []
    }
  },
  data() {
    return {
      loading: true,
      summaryData: {},
      totalSummaryRow: {},
      taskTypeSummaryList: [],

      defaultSummaryFilter: {    
        startDate: moment().local().subtract(30, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingSummaryFilter: {},
      appliedSummaryFilter: {},
    }
  },
  watch: {
    filter: function() {
      this.requestData()
    }
  },
  created() {
  },
  mounted() {
    this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
    this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

    this.loading = true
    this.requestData()
  },
  methods: {
    requestData() {
      var filter = {
        startDate: this.appliedSummaryFilter.startDate,
        endDate: this.appliedSummaryFilter.endDate,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      Promise.all([
        this.$apiService.analyticsService.getOrderDIFOTSummary(filter),
      ])
      .then(results => {
        this.summaryData = results[0].data

        let totalSummaryList = this.summaryData.summaryList

        this.totalSummaryRow = {
          orderCount: totalSummaryList.reduce((acc, summaryInfo) => acc + (summaryInfo.totalOrderCount || 0), 0),
          operationalDIFOT: 0,
          commercialDIFOT: 0,
          hitCount: totalSummaryList.reduce((acc, summaryInfo) => acc + (summaryInfo.isHit ? summaryInfo.totalOrderCount : 0), 0),
          missCount: totalSummaryList.reduce((acc, summaryInfo) => acc + (summaryInfo.isMiss ? summaryInfo.totalOrderCount : 0), 0),
          excludeCount: totalSummaryList.reduce((acc, summaryInfo) => acc + (summaryInfo.isExclude ? summaryInfo.totalOrderCount : 0), 0),
          nonClassifiedCount: totalSummaryList.reduce((acc, summaryInfo) => acc + (summaryInfo.isNotSet ? summaryInfo.totalOrderCount : 0), 0)
        }

        let totalClassified = this.totalSummaryRow.hitCount + this.totalSummaryRow.missCount + this.totalSummaryRow.excludeCount
        let totalOperational = this.totalSummaryRow.hitCount + this.totalSummaryRow.missCount

        if(totalOperational > 0) {
          this.totalSummaryRow.operationalDIFOT = (this.totalSummaryRow.hitCount / totalOperational) * 100
        }
        if(totalClassified > 0) {
          this.totalSummaryRow.commercialDIFOT = (this.totalSummaryRow.hitCount / totalClassified) * 100
        }        

        this.taskTypeSummaryList = this.summaryData.taskTypeList.map(taskTypeInfo => {
          let taskTypeSummaryList = totalSummaryList.filter(summaryInfo => summaryInfo.despatchTaskTypeId === taskTypeInfo.id)

          let totalOrderCount = taskTypeSummaryList.reduce((acc, summaryInfo) => acc + (summaryInfo.totalOrderCount || 0), 0)
          let totalHitCount = taskTypeSummaryList.reduce((acc, summaryInfo) => acc + (summaryInfo.isHit ? summaryInfo.totalOrderCount : 0), 0)
          let totalMissCount = taskTypeSummaryList.reduce((acc, summaryInfo) => acc + (summaryInfo.isMiss ? summaryInfo.totalOrderCount : 0), 0)
          let totalExcludeCount = taskTypeSummaryList.reduce((acc, summaryInfo) => acc + (summaryInfo.isExclude ? summaryInfo.totalOrderCount : 0), 0)
          let totalNotSetCount = taskTypeSummaryList.reduce((acc, summaryInfo) => acc + (summaryInfo.isNotSet ? summaryInfo.totalOrderCount : 0), 0)

          let totalTaskTypeClassified = totalHitCount + totalMissCount + totalExcludeCount
          let totalTaskTypeOperational = totalHitCount + totalMissCount

          let operationalDIFOT = 0
          let commercialDIFOT = 0

          if(totalTaskTypeOperational > 0) {
            operationalDIFOT = (totalHitCount / totalTaskTypeOperational) * 100
          }
          if(totalTaskTypeClassified > 0) {
            commercialDIFOT = (totalHitCount / totalTaskTypeClassified) * 100
          }

          return {
            id: taskTypeInfo.id,
            name: taskTypeInfo.name,
            orderCount: totalOrderCount,
            operationalDIFOT: operationalDIFOT,
            commercialDIFOT: commercialDIFOT,
            hitCount: totalHitCount,
            missCount: totalMissCount,
            excludeCount: totalExcludeCount,
            nonClassifiedCount: totalNotSetCount
          }
        })
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        this.loading = false
      })
    },
    requestReportData() {
      this.loading = true

      this.requestData()
    },
    applySummaryFilter() {
      this.appliedSummaryFilter = _.clone(this.editingSummaryFilter)

      this.requestReportData()
    },
    resetSummaryFilter() {
      this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
      this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

      this.requestReportData()
    }
  }
}
</script>